// packages/web/src/lib/selfTape.ts — A6: every derivation the self-tape
// surfaces make, in one place, as pure functions.
//
// Sibling of auditionSpread.ts and farSide.ts, bound by the same rule:
//
//   THE PACK IS THE VOCABULARY. THIS FILE HOLDS NO COPY OF IT.
//
// ===================================================================
// AND HERE THAT RULE HAD A REAL EDGE TO IT
// ===================================================================
// The spec says the production treatment belongs to a self-tape and not to a
// headshot. The obvious implementation is `kind === 'self_tape'` — and that is
// a copy of the pack's vocabulary in the web, the exact thing three tranches
// of this codebase have been written to avoid.
//
// So the treatment is not keyed on the KIND AT ALL. It is keyed on the FACTS:
//
//   A TAPE IS A TAPE BECAUSE IT HAS TAKES, NOT BECAUSE IT IS LABELLED ONE.
//
// A row carrying `corner`, `takes_shot` or `take_sent` is a production record
// and gets the production treatment. A headshot carries none of them and is
// therefore quiet, without this file ever knowing the word "headshot". If the
// pack adds `screen_test_tape` next year it inherits the treatment for free,
// and if somebody records takes against a photo the product believes them —
// which is the right way round.
//
// ===================================================================
// THE DESIGN LAW: A3-A4 PROGRESS · A5 OBLIGATIONS · A6 CRAFT
// ===================================================================
// Craft is a practice with a record. The question is not "what do we have"
// but "what did we do, and did it work" — which is why the surface is a log
// of takes and not a gallery, and why `take_sent` is the fact everything
// else arranges itself around.
import { parseDay } from './aging';
import type { LinkRow, RecordRow } from './api';

// -------------------------------------------------------------------
// 1. THE LOG — newest first, because a tape HAPPENED
// -------------------------------------------------------------------

/** `recorded_at` as a sortable instant, or null. Accepts a full timestamp or
 *  a bare calendar day; a `date` is read as a LOCAL day (parseDay's own rule)
 *  so a tape shot on the 12th never sorts as the 11th. */
export function tapeInstant(row: RecordRow): number | null {
  const v = row.fields.recorded_at;
  if (typeof v !== 'string' || !v.trim()) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v.trim())) {
    const d = parseDay(v);
    return d === null ? null : d.getTime();
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

export interface TapeOrder {
  /** Newest first — the opposite of a shoot day, and for the opposite reason:
   *  a tape happened, a shoot day is coming. */
  dated: RecordRow[];
  /** No `recorded_at`. Its own group at the END, never mixed in — an undated
   *  tape is not "the newest", and sorting it to the top would put the least
   *  known thing where the eye lands first. */
  undated: RecordRow[];
}

export function orderTapes(rows: RecordRow[]): TapeOrder {
  const dated: { row: RecordRow; t: number }[] = [];
  const undated: RecordRow[] = [];
  for (const row of rows) {
    const t = tapeInstant(row);
    if (t === null) undated.push(row);
    else dated.push({ row, t });
  }
  dated.sort((a, b) => b.t - a.t);
  return { dated: dated.map((d) => d.row), undated };
}

// -------------------------------------------------------------------
// 2. THE PRODUCTION FACTS — derived from data, never from a kind name
// -------------------------------------------------------------------

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/** Was this thing MADE, in the sense the pack's §3c fields describe? True
 *  when any production fact is recorded. See this file's header for why the
 *  test is the facts and not `kind`. */
export function isProduction(row: RecordRow): boolean {
  const f = row.fields;
  return (
    num(f.takes_shot) !== null ||
    num(f.take_sent) !== null ||
    (typeof f.corner === 'string' && f.corner.trim().length > 0)
  );
}

/**
 * Should this row say that nobody recorded which take went?
 *
 * ONLY when takes were shot and no take is named. The pack calls `take_sent`
 * "the single most-forgotten fact in a self-tape household, and the one the
 * coach asks about first", so the gap is worth saying — ONCE, in words, and
 * never as an alarm. It is a hole in a record, not an emergency.
 *
 * A headshot has no `takes_shot`, so it never triggers this, and the line
 * therefore never becomes noise the parent learns to skip. That is the whole
 * reason this is a separate predicate from `isProduction`.
 */
export function missingTakeSent(row: RecordRow): boolean {
  return num(row.fields.takes_shot) !== null && num(row.fields.take_sent) === null;
}

// -------------------------------------------------------------------
// 3. WHO READ — two pointers, neither above the other
// -------------------------------------------------------------------

export type Reader =
  | { kind: 'household'; id: string; name: string }
  | { kind: 'outside'; id: string; name: string }
  | null;

/**
 * Who read off camera.
 *
 * The pack declares BOTH `media_reader_subject` (a sibling) and
 * `media_reader` (an outside person) and says why: "both exist because both
 * happen, and neither is more authoritative". So this returns whichever is
 * present and does not rank them — when both somehow exist, the household one
 * is returned first only because it is the one whose name the screen already
 * has, not because it outranks anything.
 *
 * ⚠ THE RETURNED READER IS NOT THE SUBJECT OF THE TAPE. `media_subject` is
 * ABOUTNESS — whose career this is about — and is the only pointer the read
 * scope walks. A sibling who held the pages does not thereby own the tape,
 * and any screen that blurs the two leaks one child's material into another's
 * list. The two axes are not one axis.
 */
export function readerOf(
  links: LinkRow[],
  selfId: string,
  names: { subjects: Map<string, string>; people: Map<string, string> },
  keys: { subject: string; person: string }
): Reader {
  const end = (linkType: string): string | null => {
    for (const l of links) {
      if (l.link_type !== linkType) continue;
      if (l.src_id === selfId) return l.dst_id;
      if (l.dst_id === selfId) return l.src_id;
    }
    return null;
  };
  const sid = end(keys.subject);
  if (sid) {
    const name = names.subjects.get(sid);
    if (name) return { kind: 'household', id: sid, name };
  }
  const pid = end(keys.person);
  if (pid) {
    const name = names.people.get(pid);
    if (name) return { kind: 'outside', id: pid, name };
  }
  return null;
}

// -------------------------------------------------------------------
// 4. WHAT CAME BACK — audio first, and marks in time order
// -------------------------------------------------------------------

/**
 * Does this review carry the recording itself?
 *
 * The screen asks this because the pack retains `audio_ref` AND `transcript`
 * and is explicit that the transcript must never quietly replace the
 * recording: "a coach's tone carries half the note". A review with audio
 * renders the audio FIRST; a transcript shown alone, with the recording a
 * level down or absent, is the product discarding half of what was said.
 */
export function reviewHasAudio(row: RecordRow): boolean {
  const v = row.fields.audio_ref;
  return typeof v === 'string' && v.trim().length > 0;
}

export interface TimelineMark {
  atSeconds: number;
  note: string;
}

/**
 * `timeline_marks` — a coach's notes pinned to moments in the tape.
 *
 * The field is `json`, which means the browser gets whatever was written and
 * this function is the only thing standing between that and a crash. It is
 * therefore DEFENSIVE BY CONSTRUCTION: anything that is not an object with a
 * finite `at_seconds` and a non-empty `note` is DROPPED, silently, rather
 * than rendered as a broken row or thrown.
 *
 * Dropping is right here and would be wrong elsewhere: a malformed mark is
 * not a fact about the tape that the parent needs, and one bad entry must not
 * take the other nine off the screen.
 *
 * Sorted ascending, because a note at 0:12 before a note at 1:40 is the order
 * the tape plays in — which is the only order these mean anything in.
 */
export function timelineMarks(value: unknown): TimelineMark[] {
  if (!Array.isArray(value)) return [];
  const out: TimelineMark[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const m = raw as Record<string, unknown>;
    const at = m.at_seconds;
    const note = m.note;
    if (typeof at !== 'number' || !Number.isFinite(at) || at < 0) continue;
    if (typeof note !== 'string' || !note.trim()) continue;
    out.push({ atSeconds: at, note: note.trim() });
  }
  out.sort((a, b) => a.atSeconds - b.atSeconds);
  return out;
}

/** `0:42`, `1:07`, `12:03`. Tabular by construction — the minutes never pad,
 *  the seconds always do, which is how a running time is written everywhere
 *  else a person has ever seen one. */
export function stamp(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** A `duration` field as a running time, or null. The pack stores seconds;
 *  an absent or unreadable duration renders NOTHING rather than 0:00, which
 *  would be a claim that the tape is empty. */
export function runningTime(value: unknown): string | null {
  const n = num(value);
  return n === null ? null : stamp(n);
}
